import {
    Action,
    AnyAction,
    CombinedState,
    combineReducers,
    Reducer,
    ReducersMapObject,
} from 'redux';

export interface PayloadAction<TActionType, TPayload>
    extends Action<TActionType> {
    payload: TPayload;
    storePath: string;
}

export const createAction = <TActionType, TPayload>(
    type: TActionType,
): ((
    payload: TPayload,
    storePath?: string,
) => PayloadAction<TActionType, TPayload>) =>
    function (
        payload: TPayload,
        storePath?: string,
    ): PayloadAction<TActionType, TPayload> {
        return {
            type,
            payload,
            storePath,
        };
    };

interface ReducerBuilder<TActionType, TState> {
    add: <TPayload>(
        _type: TActionType,
        fn: (
            state: TState,
            action: PayloadAction<TActionType, TPayload>,
        ) => TState,
    ) => ReducerBuilder<TActionType, TState>;
}

interface ReducerPair<TActionType, TState> {
    reducer: Reducer<TState>;
    builder: ReducerBuilder<TActionType, TState>;
}

export const createReducer = <TActionType extends string, TState>(
    state: TState,
): ReducerPair<TActionType, TState> => {
    const fns: {
        [key: string]: (state: TState, action: AnyAction) => TState;
    } = {};

    const _reducer: Reducer<TState> = function (
        _state: TState = state,
        _action: PayloadAction<TActionType, any> = undefined,
    ): TState {
        if (!_action) {
            return _state;
        }
        const fn = fns[_action.type];
        if (fn) {
            return fn(_state, _action);
        }
        return _state;
    };

    const _builder: ReducerBuilder<TActionType, TState> = {
        add<TPayload>(
            _type: TActionType,
            fn: (
                state: TState,
                action: PayloadAction<TActionType, TPayload>,
            ) => TState,
        ): ReducerBuilder<TActionType, TState> {
            fns[_type] = fn;
            return this;
        },
    };

    return { reducer: _reducer, builder: _builder };
};

export const createFilteredReducer = <TActionType extends string, TState>(
    reducer: Reducer<TState>,
    predicate: (action: PayloadAction<TActionType, any>) => boolean,
): ((state: TState, action: PayloadAction<TActionType, any>) => TState) => {
    return (state, action): TState => {
        return predicate(action) || !state ? reducer(state, action) : state;
    };
};

function combine<TState>(reducers: ReducersMapObject<TState>): Reducer<TState> {
    const keys = Object.keys(reducers);
    const combined = keys.reduce<ReducersMapObject>((p, e) => {
        if (typeof reducers[e] !== 'function') {
            p[e] = combine(reducers[e]);
        } else {
            p[e] = reducers[e];
        }
        return p;
    }, {});
    return combineReducers(combined);
}

function insert<TState>(
    path: string,
    reducer: Reducer<TState>,
    reducers: ReducersMapObject<TState>,
): void {
    let next = reducers;
    const parts = path.split('.');
    parts.forEach((e, i) => {
        if (i === parts.length - 1) {
            return;
        }
        next[e] = next[e] || {};
        next = next[e];
    });
    const key = parts[parts.length - 1];
    next[key] = reducer;
}

interface ReducerManager {
    reduce: <TState>(state: TState, action: AnyAction) => Reducer<TState>;

    add: <TState>(path: string, reducer: Reducer<TState>) => void;
}

export const createReducerManager = (
    initialReducers: ReducersMapObject,
): ReducerManager => {
    const reducers: ReducersMapObject = { ...initialReducers };
    let combinedReducer = combineReducers(reducers);

    return {
        reduce<TState>(
            state: TState,
            action: AnyAction,
        ): Reducer<CombinedState<TState>> {
            return combinedReducer(state, action);
        },
        add<TState>(path: string, reducer: Reducer<TState>): void {
            insert(path, reducer, reducers);
            combinedReducer = combine(reducers);
        },
    };
};
